import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { UserIcon } from '@heroicons/react/24/outline';

interface ProfileData {
  firstName: string;
  lastName: string;
  timezone: string;
  bio: string;
}

interface ProfileCompletionPromptProps {
  open: boolean;
  onQuickSetup: (data: ProfileData) => Promise<void>;
  userType: 'client' | 'professional';
}

export default function ProfileCompletionPrompt({
  open,
  onQuickSetup,
  userType
}: ProfileCompletionPromptProps) {
  const isClient = userType === 'client';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    timezone: '',
    bio: ''
  });

  const sanitizeInput = (value: string, type: 'name' | 'timezone' | 'bio'): string => {
    let sanitized = value;

    // Only remove null bytes and dangerous control characters, preserve normal whitespace
    sanitized = sanitized.replace(/[\x00-\x08\x0E-\x1F\x7F]/g, '');

    // Remove HTML tags and entities to prevent XSS
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    sanitized = sanitized.replace(/&[#\w]+;/g, '');

    if (type === 'name') {
      // For names: allow letters, spaces, hyphens, apostrophes, limit length
      sanitized = sanitized.replace(/[^a-zA-Z\s'-]/g, '');
      sanitized = sanitized.slice(0, 50);
      // Remove excessive whitespace
      sanitized = sanitized.replace(/\s+/g, ' ').trim();
    } else if (type === 'timezone') {
      // For timezone: only allow valid timezone format
      sanitized = sanitized.replace(/[^a-zA-Z0-9/_-]/g, '');
      sanitized = sanitized.slice(0, 50);
    } else if (type === 'bio') {
      // Enhanced security for bio field while preserving natural text input

      // Block dangerous patterns first
      const dangerousPatterns = [
        /javascript:/i,
        /<script/i,
        /onload=/i,
        /onerror=/i,
        /onclick=/i,
        /onmouseover=/i,
        /onfocus=/i,
        /eval\(/i,
        /expression\(/i,
        /vbscript:/i,
        /data:/i
      ];

      for (const pattern of dangerousPatterns) {
        if (pattern.test(sanitized)) {
          return ''; // Clear field if dangerous pattern detected
        }
      }

      // Remove only the most dangerous characters, preserve spaces and common punctuation
      // Remove: < > { } to prevent tag injection and code blocks
      sanitized = sanitized.replace(/[<>{}]/g, '');

      // Limit length
      sanitized = sanitized.slice(0, 500);

      // Don't modify whitespace during typing - let user type naturally
    }

    return sanitized;
  };

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    let sanitizedValue = value;

    if (field === 'firstName' || field === 'lastName') {
      sanitizedValue = sanitizeInput(value, 'name');
    } else if (field === 'timezone') {
      sanitizedValue = sanitizeInput(value, 'timezone');
    } else if (field === 'bio') {
      sanitizedValue = sanitizeInput(value, 'bio');
    }

    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  const handleQuickSetup = async () => {
    setLoading(true);
    try {
      await onQuickSetup(formData);
      // Don't call onClose() here - let the parent component handle closing
    } catch (error) {
      console.error('Failed to save profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/20 p-0 text-left align-middle shadow-2xl ring-1 ring-black/5 transition-all">
                {/* Header Section with Gradient */}
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 px-8 py-6 text-white">
                  <div className="flex items-center">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
                      <UserIcon className="h-7 w-7 text-white" aria-hidden="true" />
                    </div>
                    <div className="ml-4">
                      <Dialog.Title as="h3" className="text-xl font-bold leading-6 text-white">
                        🎉 Welcome to Wonderstood!
                      </Dialog.Title>
                      <p className="text-indigo-100 text-sm mt-1">
                        Let's get your profile set up in just 2 minutes
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form Section */}
                <div className="px-8 py-8">
                  <div className="mb-6">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Complete your profile to help {isClient ? 'professionals understand how to best assist you' : 'clients discover your expertise'}.
                    </p>
                  </div>

                  <form className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-semibold text-gray-800 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="block w-full rounded-xl border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:bg-white transition-all duration-200"
                          placeholder="Enter your first name"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-semibold text-gray-800 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="block w-full rounded-xl border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:bg-white transition-all duration-200"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="timezone" className="block text-sm font-semibold text-gray-800 mb-2">
                        Timezone
                      </label>
                      <select
                        id="timezone"
                        value={formData.timezone}
                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                        className="block w-full rounded-xl border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:bg-white transition-all duration-200"
                      >
                        <option value="">Select your timezone</option>
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Anchorage">Alaska Time (AKT)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                        <option value="America/Toronto">Toronto (ET)</option>
                        <option value="America/Vancouver">Vancouver (PT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="Europe/Paris">Paris (CET)</option>
                        <option value="Europe/Berlin">Berlin (CET)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Shanghai (CST)</option>
                        <option value="Asia/Kolkata">Mumbai (IST)</option>
                        <option value="Australia/Sydney">Sydney (AEDT)</option>
                        <option value="Australia/Melbourne">Melbourne (AEDT)</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="bio" className="block text-sm font-semibold text-gray-800 mb-2">
                        About You
                      </label>
                      <textarea
                        id="bio"
                        rows={3}
                        value={formData.bio}
                        onChange={(e) => handleInputChange('bio', e.target.value)}
                        className="block w-full rounded-xl border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:bg-white transition-all duration-200 resize-none"
                        placeholder={isClient
                          ? "Tell professionals about yourself and what kind of help you're looking for..."
                          : "Tell potential clients about your expertise and experience..."
                        }
                      />
                    </div>
                  </form>
                </div>

                {/* Action Buttons */}
                <div className="px-8 pb-8 flex justify-center">
                  <button
                    type="button"
                    onClick={handleQuickSetup}
                    disabled={loading || !formData.firstName || !formData.lastName || !formData.timezone}
                    className="px-8 inline-flex justify-center items-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:from-indigo-700 hover:to-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save & Continue'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}